import React, { useMemo } from "react";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { useCashFlowStatementQuery } from "@api/accountingReports.api";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { DownloadDropdown } from "../balanceSheet/DownloadDropdown";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { cashFlowFilterDefinition } from "../accountingReportFilterDefinitions";
import { AccountingReportViewToolbar } from "../reportViews/AccountingReportViewToolbar";
import { useAccountingTabContext } from "../useAccountingTab";
import { useGlDrilldownDrawerState } from "../useGlDrilldownDrawerState";
import { useCashFlowStatementDownload } from "./useCashFlowStatementDownload";
import { CashFlowStatementContent } from "./CashFlowStatementContent";

export const CashFlowStatementPage: React.FC = () => {
    const { filters, setFilters, selectedEntity, ledgerBasis } =
        useAccountingTabContext();
    const {
        openDrillDown,
        closeDrillDown,
        isOpen: isGlDrawerOpen,
        drawerKey,
        drawerTarget,
    } = useGlDrilldownDrawerState();
    const filtersWithDefaultValues = useMemo(() => {
        if (filters.cycle === undefined) {
            return {
                ...filters,
                cycle: cashFlowFilterDefinition.defaultCycle,
            };
        }
        return filters;
    }, [filters]);

    const { cashFlowStatement, generationDate } = useCashFlowStatementQuery(
        filtersWithDefaultValues,
    );

    const { isDownloading, download } = useCashFlowStatementDownload(
        filtersWithDefaultValues,
        cashFlowStatement.data?.presentation?.rowVisibility,
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_CASH_FLOW_STATEMENT_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <>
            <AccountingReportViewToolbar
                reportId="cashFlowStatement"
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
            >
                <DownloadDropdown
                    onDownload={(format) =>
                        selectedEntity &&
                        download({
                            entity: selectedEntity,
                            format,
                            startDate: filtersWithDefaultValues.startDate,
                            endDate: filtersWithDefaultValues.endDate,
                        })
                    }
                    loading={isDownloading}
                    disabled={
                        cashFlowStatement.isLoading ||
                        !cashFlowStatement.isSuccess
                    }
                />
            </AccountingReportViewToolbar>
            <AccountingReportFiltersForm
                {...cashFlowFilterDefinition.filterProps}
                showFinancialAccountFilter
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                shouldTrackDatePreset
                hasEntityGrouping
                hasClassGrouping
                requireDateSelection
                hideSearch
                showCyclesSelect
            />
            <LoaderOnInitialLoading
                queryResult={cashFlowStatement}
                dimWhileFetching
            >
                {cashFlowStatement.data && selectedEntity && (
                    <CashFlowStatementContent
                        report={cashFlowStatement.data}
                        filters={filtersWithDefaultValues}
                        chrome={cashFlowStatement.data.presentation.chrome}
                        numberFormat={
                            cashFlowStatement.data.presentation.numberFormat
                        }
                        generationDate={generationDate}
                        entity={selectedEntity}
                        onDrillDown={openDrillDown}
                    />
                )}
            </LoaderOnInitialLoading>
            {drawerTarget && (
                <GlDrilldownDrawer
                    key={drawerKey}
                    open={isGlDrawerOpen}
                    onClose={closeDrillDown}
                    filters={drawerTarget.filters}
                    entityIds={drawerTarget.entityIds}
                    ledgerBasis={ledgerBasis}
                    classifications={drawerTarget.classifications}
                    pnlBalanceSince={drawerTarget.pnlBalanceSince}
                />
            )}
        </>
    );
};
